/**
 * T247.F — Scorer marketplace portfolio editor.
 *
 * Route: /account/portfolio/
 *
 * Customer-facing edit page for the public scorer-for-hire profile.
 * B2C_ELITE perk — the backend rejects apply() unless the user has
 * an active Elite subscription, but we surface a friendly upgrade
 * prompt at render time to avoid the round-trip.
 *
 * Status banner at the top shows DRAFT / PENDING / LISTED / DENIED /
 * WITHDRAWN with the right CTA for each state.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Send, Upload, ExternalLink, CheckCircle2,
  Clock, XCircle, AlertCircle, Sparkles,
} from "lucide-react";

import useSubscriptionStore from "../../store/subscription.store";
import {
  applyForListing, getMyProfile, updateMyProfile,
  uploadProfilePhoto, withdrawListing,
} from "../../api/scorerMarketplace.api";


const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "hi", label: "Hindi" },
  { code: "kn", label: "Kannada" },
  { code: "te", label: "Telugu" },
  { code: "ml", label: "Malayalam" },
  { code: "mr", label: "Marathi" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
];

const FORMAT_OPTIONS = ["T20", "ODI", "TEST", "HUNDRED", "T10", "T15"];


function StatusBanner({ profile, onApply, onWithdraw, busy }) {
  if (!profile) return null;
  const s = profile.status;

  if (s === "LISTED") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 mb-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-green-900">You're listed.</div>
            <p className="text-sm text-green-800 mb-2">
              Your profile is live on the public Find-a-Scorer marketplace.
              Tournament organisers can find you.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Link to={`/find-a-scorer/${profile.id}`} target="_blank"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border rounded text-sm hover:bg-gray-50">
                <ExternalLink className="w-3.5 h-3.5" /> View public profile
              </Link>
              <button onClick={onWithdraw} disabled={busy}
                      className="px-3 py-1.5 border rounded text-sm bg-white hover:bg-gray-50">
                Withdraw listing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (s === "PENDING") {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 mb-4">
        <div className="flex items-start gap-2">
          <Clock className="w-5 h-5 text-yellow-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-yellow-900">Awaiting review.</div>
            <p className="text-sm text-yellow-800 mb-2">
              Submitted {profile.applied_at && new Date(profile.applied_at).toLocaleDateString()}.
              We review most profiles within 2 business days.
            </p>
            <button onClick={onWithdraw} disabled={busy}
                    className="px-3 py-1.5 border rounded text-sm bg-white hover:bg-gray-50">
              Withdraw submission
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (s === "DENIED") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4">
        <div className="flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">Not listed.</div>
            <p className="text-sm text-red-800 mb-2">
              Your last submission wasn't approved. Edit your profile below and
              re-submit when ready.
            </p>
            <button onClick={onApply} disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              <Send className="w-3.5 h-3.5" /> Re-submit for review
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (s === "WITHDRAWN") {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-gray-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-gray-900">Withdrawn.</div>
            <p className="text-sm text-gray-700 mb-2">
              Your profile is not listed. Re-submit any time.
            </p>
            <button onClick={onApply} disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              <Send className="w-3.5 h-3.5" /> Re-submit for review
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DRAFT
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 mb-4">
      <div className="flex items-start gap-2">
        <Sparkles className="w-5 h-5 text-blue-700 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-blue-900">Build your public profile.</div>
          <p className="text-sm text-blue-800 mb-2">
            Fill in your details below, then submit for review. Once approved,
            you'll appear on the public Find-a-Scorer marketplace where
            tournament organisers can hire you.
          </p>
          <button onClick={onApply} disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            <Send className="w-3.5 h-3.5" /> Submit for review
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Portfolio() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const subs = useSubscriptionStore((s) => s.list);
  const fetchSubs = useSubscriptionStore((s) => s.fetch);
  useEffect(() => { if (typeof fetchSubs === "function") fetchSubs(); }, [fetchSubs]);

  const hasElite = (subs || []).some(
    (s) => s.status === "ACTIVE" && !s.is_trial &&
           (s.plan?.code === "B2C_ELITE" || s.plan_code === "B2C_ELITE"),
  );

  async function load() {
    setLoading(true);
    try {
      const { data } = await getMyProfile();
      setProfile(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function setField(name, value) {
    setProfile((p) => ({ ...p, [name]: value }));
  }

  function toggleInArray(name, value) {
    setProfile((p) => {
      const cur = p[name] || [];
      return {
        ...p,
        [name]: cur.includes(value)
          ? cur.filter((x) => x !== value)
          : [...cur, value],
      };
    });
  }

  async function save() {
    setError("");
    setSaveStatus("Saving…");
    try {
      const { data } = await updateMyProfile({
        display_name: profile.display_name,
        bio: profile.bio,
        years_experience: parseInt(profile.years_experience, 10) || 0,
        languages: profile.languages || [],
        regions: profile.regions || [],
        formats_scored: profile.formats_scored || [],
        daily_rate_inr: profile.daily_rate_inr ? parseInt(profile.daily_rate_inr, 10) : null,
        sample_match_urls: (profile.sample_match_urls || []).filter(Boolean),
        contact_email: profile.contact_email,
        contact_phone: profile.contact_phone,
      });
      setProfile(data);
      setSaveStatus("Saved");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
      setSaveStatus("");
    }
  }

  async function onPhotoChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await uploadProfilePhoto(f);
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onApply() {
    setBusy(true);
    setError("");
    try {
      const { data } = await applyForListing();
      setProfile(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submit failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onWithdraw() {
    if (!window.confirm("Pull your listing from the public marketplace?")) return;
    setBusy(true);
    try {
      const { data } = await withdrawListing();
      setProfile(data);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading…</div>;
  if (!profile) return null;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
        <ChevronLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <h1 className="text-2xl font-bold mb-1">Public profile</h1>
      <p className="text-sm text-gray-600 mb-4">
        Your scorer-for-hire profile on the Find-a-Scorer marketplace.
        Available as part of the ZCD Elite plan.
      </p>

      {!hasElite && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 text-orange-700 mt-0.5" />
            <div>
              <div className="font-semibold text-orange-900">Elite plan required to apply.</div>
              <p className="text-orange-800">
                The marketplace listing is part of the ZCD Elite plan (₹6,999/mo).
                You can still edit your profile here — submit for review once you upgrade.
              </p>
              <Link to="/subscriptions" className="inline-block mt-2 px-3 py-1 bg-orange-600 text-white rounded">
                Upgrade plan
              </Link>
            </div>
          </div>
        </div>
      )}

      <StatusBanner profile={profile} onApply={onApply} onWithdraw={onWithdraw} busy={busy} />

      {error && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800 mb-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      <div className="space-y-4">

        {/* Photo */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Profile photo</h2>
          <div className="flex items-center gap-4">
            {profile.photo_url
              ? <img src={profile.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border" />
              : <div className="w-24 h-24 rounded-full bg-gray-100 border flex items-center justify-center text-gray-400 text-xs">No photo</div>}
            <div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhotoChange} className="hidden" />
              <button onClick={() => fileRef.current?.click()} disabled={busy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border rounded text-sm hover:bg-gray-50">
                <Upload className="w-3.5 h-3.5" /> Upload photo
              </button>
              <p className="text-xs text-gray-500 mt-1">JPG / PNG / WebP, max 4 MB.</p>
            </div>
          </div>
        </div>

        {/* Basics */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-3">Basics</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Display name</span>
              <input type="text" value={profile.display_name || ""}
                     onChange={(e) => setField("display_name", e.target.value)}
                     className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Years scoring</span>
              <input type="number" min="0" max="60" value={profile.years_experience || 0}
                     onChange={(e) => setField("years_experience", e.target.value)}
                     className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium">Bio</span>
              <textarea rows={4} maxLength={2000} value={profile.bio || ""}
                        onChange={(e) => setField("bio", e.target.value)}
                        className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                        placeholder="What kind of matches you score, your strengths, any notable tournaments…" />
              <span className="text-xs text-gray-500">{(profile.bio || "").length}/2000</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Day rate (₹) — optional</span>
              <input type="number" min="0" value={profile.daily_rate_inr || ""}
                     onChange={(e) => setField("daily_rate_inr", e.target.value)}
                     className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
          </div>
        </div>

        {/* Languages */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Languages</h2>
          <div className="flex flex-wrap gap-2">
            {LANGUAGE_OPTIONS.map((l) => {
              const on = (profile.languages || []).includes(l.code);
              return (
                <button key={l.code} type="button"
                        onClick={() => toggleInArray("languages", l.code)}
                        className={`px-3 py-1 rounded border text-sm ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white hover:bg-gray-50"}`}>
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Regions */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Regions you work in</h2>
          <input type="text"
                 value={(profile.regions || []).join(", ")}
                 onChange={(e) => setField("regions", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                 className="w-full border rounded px-2 py-1.5 text-sm"
                 placeholder="Chennai, Bengaluru, Hyderabad" />
          <p className="text-xs text-gray-500 mt-1">Comma-separated. City or state names.</p>
        </div>

        {/* Formats */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Formats you've scored</h2>
          <div className="flex flex-wrap gap-2">
            {FORMAT_OPTIONS.map((f) => {
              const on = (profile.formats_scored || []).includes(f);
              return (
                <button key={f} type="button"
                        onClick={() => toggleInArray("formats_scored", f)}
                        className={`px-3 py-1 rounded border text-sm ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white hover:bg-gray-50"}`}>
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sample matches */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Sample matches (up to 5)</h2>
          <p className="text-xs text-gray-500 mb-2">Public live-link URLs from matches you've coded on ZCD.</p>
          {[0, 1, 2, 3, 4].map((i) => (
            <input key={i} type="url"
                   value={(profile.sample_match_urls || [])[i] || ""}
                   onChange={(e) => {
                     const arr = [...(profile.sample_match_urls || [])];
                     arr[i] = e.target.value;
                     setField("sample_match_urls", arr);
                   }}
                   placeholder="https://www.zencricketdata.com/live/..."
                   className="block w-full border rounded px-2 py-1.5 text-sm mb-2" />
          ))}
        </div>

        {/* Contact */}
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-3">Contact</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Contact email</span>
              <input type="email" value={profile.contact_email || ""}
                     onChange={(e) => setField("contact_email", e.target.value)}
                     className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Contact phone</span>
              <input type="tel" value={profile.contact_phone || ""}
                     onChange={(e) => setField("contact_phone", e.target.value)}
                     className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
            </label>
          </div>
        </div>

        {/* Save bar */}
        <div className="sticky bottom-0 bg-white border-t -mx-4 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-500">{saveStatus}</span>
          <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
